import { FolderOpen, PlayCircle } from 'lucide-react';

interface WelcomeModalProps {
  userName?: string;
  /** Play the guided demo on a sample job. */
  onWatchDemo: () => void;
  /** Go straight to naming their own first project. */
  onSkip: () => void;
  onOpenExisting: () => void;
}

/**
 * First-run greeting for accounts with no projects.
 *
 * The demo runs on a sample job rather than their own, so this asks nothing of
 * them up front — no project name, no fields. Watch a complete evaluation
 * first, then set up the real one. Skipping is remembered; nobody is asked twice.
 */
export function WelcomeModal({ userName, onWatchDemo, onSkip, onOpenExisting }: WelcomeModalProps) {
  const firstName = userName?.split(' ')[0];

  return (
    <div data-testid="welcome-modal" className="fixed inset-0 bg-gradient-to-br from-blue-900/80 to-gray-900/80 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden">

        {/* Header band */}
        <div className="bg-blue-600 px-8 py-6 text-white">
          <div className="flex items-center space-x-3 mb-3">
            <img
              src="/trueload_logo.svg"
              alt="TrueLoad"
              height={40}
              className="h-10 w-auto"
              style={{ imageRendering: 'auto' }}
            />
          </div>
          <h1 className="text-2xl font-bold leading-tight">
            {firstName ? `Welcome, ${firstName}!` : 'Welcome to TrueLoad!'}
          </h1>
          <p className="mt-1 text-blue-100 text-sm">
            Service Adequacy Evaluations · NEC 220.87 & 220.83
          </p>
        </div>

        {/* Body */}
        <div className="px-8 py-6">
          <p className="text-gray-700 text-sm leading-relaxed mb-2">
            TrueLoad proves what a service can actually carry — from the meter's own demand
            record, or from the code's traditional calculation when there is no data to wait
            for — so you can show a customer and an inspector real numbers instead of
            assumptions.
          </p>
          <p className="text-gray-500 text-sm leading-relaxed mb-6">
            The quickest way to see it is to watch one. We'll run a complete evaluation on a
            sample job, from an empty form to a finished report; partway through, you pick
            which method it uses. Nothing is saved, and your own work is untouched.
          </p>

          <button
            type="button"
            onClick={onWatchDemo}
            data-testid="btn-welcome-demo"
            className="w-full flex items-center justify-center gap-2 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors text-sm"
          >
            <PlayCircle className="w-4 h-4" />
            Show me a complete evaluation
          </button>
          <p className="mt-1.5 text-center text-xs text-gray-400">
            Recommended · a few minutes, and you can stop any time
          </p>

          <button
            type="button"
            onClick={onSkip}
            data-testid="btn-welcome-close"
            className="mt-2 w-full py-2.5 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
          >
            Skip — set up my first job
          </button>
        </div>

        {/* Footer */}
        <div className="px-8 pb-6 pt-0">
          <div className="border-t border-gray-100 pt-4 text-center">
            <button
              type="button"
              onClick={onOpenExisting}
              className="inline-flex items-center space-x-1.5 text-sm text-gray-500 hover:text-blue-600 transition-colors"
            >
              <FolderOpen className="w-4 h-4" />
              <span>Open an existing project instead</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
